import type { Language } from "../../i18n";

type GreetingList = string[];

const greetings: Record<Language, {
    monday: GreetingList;
    friday: GreetingList;
    morning: GreetingList;
    afternoon: GreetingList;
    evening: GreetingList;
}> = {
    en: {
        monday: [
            "Happy Monday, {name}",
            "Great Monday, {name}",
            "New week, {name}!",
        ],
        friday: [
            "Happy Friday, {name}",
            "It's Friday, {name}!",
            "Great Friday, {name}",
        ],
        morning: [
            "Good morning, {name}",
            "Morning, {name}",
            "Hey {name}",
            "Welcome, {name}",
            "Hi {name}",
        ],
        afternoon: [
            "Good afternoon, {name}",
            "Afternoon, {name}",
            "Hey {name}",
            "Hi {name}",
            "Welcome, {name}",
        ],
        evening: [
            "Good evening, {name}",
            "Evening, {name}",
            "Hey {name}",
            "Hi {name}",
        ],
    },
    pl: {
        monday: [
            "Dobry poniedziałek, {name}",
            "Nowy tydzień, {name}!",
            "Miłego poniedziałku, {name}",
        ],
        friday: [
            "Piątek, {name}!",
            "Udanego piątku, {name}",
            "Miłego piątku, {name}",
        ],
        morning: [
            "Dzień dobry, {name}",
            "Cześć {name}",
            "Witaj {name}",
            "Hej {name}",
            "Miłego dnia, {name}",
        ],
        afternoon: [
            "Dzień dobry, {name}",
            "Cześć {name}",
            "Witaj {name}",
            "Hej {name}",
        ],
        evening: [
            "Dobry wieczór, {name}",
            "Witaj {name}",
            "Cześć {name}",
            "Hej {name}",
        ],
    },
};

// Pick a stable random index per session using sessionStorage
const getSessionRandom = (): number => {
    const key = "jli-greeting-seed";
    let seed = sessionStorage.getItem(key);
    if (!seed) {
        seed = String(Math.random());
        sessionStorage.setItem(key, seed);
    }
    return parseFloat(seed);
};

const pick = (list: GreetingList, seed: number): string => {
    return list[Math.floor(seed * list.length) % list.length];
};

export const getGreeting = (name: string, language: Language): string => {
    const now = new Date();
    const day = now.getDay(); // 0=Sun, 1=Mon, 5=Fri
    const hour = now.getHours();
    const seed = getSessionRandom();
    const lang = greetings[language];

    // ~30% chance of day-specific greeting on Mon/Fri
    if (day === 1 && seed < 0.3) {
        return pick(lang.monday, seed).replace("{name}", name);
    }
    if (day === 5 && seed < 0.3) {
        return pick(lang.friday, seed).replace("{name}", name);
    }

    // Time-based fallback
    let pool: GreetingList;
    if (hour < 12) {
        pool = lang.morning;
    } else if (hour < 17) {
        pool = lang.afternoon;
    } else {
        pool = lang.evening;
    }

    return pick(pool, seed).replace("{name}", name);
};
